/**
 * `validateRow` and `classifyRow` — Phase 6 plan §2 U75. Both are pure: no I/O, no clock.
 *
 * `apps/worker/src/jobs/import-validate.ts` imports `validateRow` by value and hands it to the
 * `packages/db` driver, so `check:promised-delivery` sees an importer under `apps/*`.
 * A row is validated against its mapping and a context the driver loads once per import.
 * Nothing here ever writes a corrected GTIN as a value — the corrected number is `detail`.
 */
import { checkAIValue, hasValidCheckDigit, withCheckDigit } from "@packagepage/gs1";
import { IMPORT_FIELD_REGISTRY, } from "./import.js";
import { planRank, TIERS } from "./tiers.js";
import { permitsPublishing } from "./verification.js";
const MANGLED = /^\d+(\.\d+)?e[+-]?\d+$/i;
const LANG_TAG = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/;
const MEASUREMENT = /^(\d+(?:[.,]\d+)?)\s*([\p{L}µ]+(?:\s[\p{L}µ]+)?)$/u;
/** Excel's 2000-01-01 and 2100-01-01. A narrower range than any real date column needs. */
const SERIAL_MIN = 36526;
const SERIAL_MAX = 73051;
/** Days from 1970-01-01 back to 1899-12-30, the epoch that absorbs Excel's phantom 1900-02-29. */
const EXCEL_EPOCH_BEFORE_UNIX = 25569;
const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
function isRealDate(y, m, d) {
    if (m < 1 || m > 12 || d < 1)
        return false;
    const days = [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return d <= days[m - 1];
}
const pad = (n, width) => String(n).padStart(width, "0");
const iso = (y, m, d) => `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;
/** Days since 1970-01-01 to a civil date (proleptic Gregorian), by integer arithmetic. */
function civilFromDays(days) {
    const z = days + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) /
        365);
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const m = mp < 10 ? mp + 3 : mp - 9;
    return { y: yoe + era * 400 + (m <= 2 ? 1 : 0), m, d };
}
/**
 * An expiry or manufacture date as ISO text.
 *
 * An all-digits value is an Excel serial only inside SERIAL_MIN..SERIAL_MAX, because the xlsx
 * reader never opens `styles.xml` and a bare number carries nothing marking it a date. The
 * floor at 2000 keeps `1899-12-30 + serial` exact: below serial 61 it is a day early.
 */
export function readImportDate(text, order) {
    const t = text.trim();
    const fail = (detail) => ({ ok: false, detail });
    if (/^\d+$/.test(t)) {
        const serial = Number(t);
        if (serial < SERIAL_MIN || serial > SERIAL_MAX) {
            return fail(`The number ${t} is not a date. An Excel date between 2000 and 2100 is a number from ${String(SERIAL_MIN)} to ${String(SERIAL_MAX)}.`);
        }
        const { y, m, d } = civilFromDays(serial - EXCEL_EPOCH_BEFORE_UNIX);
        return { ok: true, iso: iso(y, m, d) };
    }
    const match = /^(\d{1,4})([/.-])(\d{1,2})\2(\d{1,4})$/.exec(t);
    if (match === null)
        return fail(`"${t}" is not a date this import can read.`);
    const [, a, , b, c] = match;
    const real = (y, m, d) => isRealDate(y, m, d)
        ? { ok: true, iso: iso(y, m, d) }
        : fail(`"${t}" is not a real date.`);
    if (a.length === 4) {
        if (c.length > 2)
            return fail(`"${t}" is not a date this import can read.`);
        return real(Number(a), Number(b), Number(c));
    }
    if (a.length > 2)
        return fail(`"${t}" is not a date this import can read.`);
    if (order === "ymd") {
        if (c.length > 2)
            return fail(`"${t}" is not a date this import can read.`);
        return real(2000 + Number(a), Number(b), Number(c));
    }
    if (c.length !== 2 && c.length !== 4)
        return fail(`"${t}" is not a date this import can read.`);
    const year = c.length === 2 ? 2000 + Number(c) : Number(c);
    const first = Number(a);
    const second = Number(b);
    if (order === "dmy")
        return real(year, second, first);
    if (order === "mdy")
        return real(year, first, second);
    if (first > 12 && second <= 12)
        return real(year, second, first);
    if (second > 12 && first <= 12)
        return real(year, first, second);
    if (first === second && first <= 12)
        return real(year, first, second);
    if (first <= 12 && second <= 12) {
        return fail(`"${t}" could be ${String(first)} ${MONTHS[second - 1]} ${String(year)} (day first) or ` +
            `${String(second)} ${MONTHS[first - 1]} ${String(year)} (month first), and no date order is set.`);
    }
    return fail(`"${t}" is not a real date.`);
}
/** The covering prefix that decides a GTIN: the longest one it starts with, as `coveringPrefixMatch` orders them. */
export function coveringImportPrefix(gtin14, prefixes) {
    const body = gtin14.slice(1);
    let best = null;
    for (const p of prefixes) {
        if (body.startsWith(p.prefix) &&
            (best === null || p.prefix.length > best.prefix.length)) {
            best = p;
        }
    }
    return best;
}
function readMeasurement(text) {
    const match = MEASUREMENT.exec(text.trim());
    if (match === null)
        return null;
    return { value: Number(match[1].replace(",", ".")), unit: match[2] };
}
function outsidePrefixDetail(gtin14, context) {
    const cover = coveringImportPrefix(gtin14, context.prefixes);
    const verified = context.prefixes.filter((p) => permitsPublishing(p.verification));
    const own = verified.length === 0
        ? "This account has no verified company prefix."
        : `This account's verified prefixes: ${verified.map((p) => p.prefix).join(", ")}.`;
    if (cover !== null) {
        return `GTIN ${gtin14} is covered by prefix ${cover.prefix}, which is ${cover.verification}, not verified. ${own}`;
    }
    return `GTIN ${gtin14} starts with prefix digits ${gtin14.slice(1, 8)}, which no prefix on this account covers. ${own}`;
}
/**
 * The GTIN `cell` would be with its last digit recomputed, or null when the digit already
 * holds or the cell is not 1 to 14 digits. Information only: no path writes it as a value.
 */
export function correctedGtin14(cell) {
    const t = cell.trim();
    if (!/^\d{1,14}$/.test(t))
        return null;
    const gtin14 = t.padStart(14, "0");
    return hasValidCheckDigit(gtin14) ? null : withCheckDigit(gtin14.slice(0, 13));
}
function readGtin(cell, context, errors) {
    const t = cell.trim();
    if (t === "") {
        errors.push({ kind: "invalid_value", detail: "The GTIN is empty." });
        return undefined;
    }
    if (MANGLED.test(t)) {
        errors.push({
            kind: "excel_mangled",
            detail: `"${t}" is a GTIN that Excel turned into scientific notation. Its digits are gone, so we cannot recover it. Save the column as text and import again.`,
        });
        return undefined;
    }
    if (!/^\d+$/.test(t)) {
        errors.push({
            kind: "invalid_value",
            detail: `"${t}" is not a GTIN. A GTIN is digits only.`,
        });
        return undefined;
    }
    if (t.length > 14) {
        errors.push({
            kind: "invalid_value",
            detail: `"${t}" has ${String(t.length)} digits. A GTIN has at most 14.`,
        });
        return undefined;
    }
    const gtin14 = t.padStart(14, "0");
    const corrected = correctedGtin14(t);
    if (corrected !== null) {
        errors.push({
            kind: "check_digit",
            detail: `The last digit of a GTIN is a check digit computed from the other thirteen. ` +
                `For ${gtin14.slice(0, 13)} it is ${corrected.slice(13)}, which makes the GTIN ` +
                `${corrected}. Check the number against your source before accepting that.`,
        });
    }
    const cover = coveringImportPrefix(gtin14, context.prefixes);
    if (cover === null || !permitsPublishing(cover.verification)) {
        errors.push({ kind: "outside_prefix", detail: outsidePrefixDetail(gtin14, context) });
    }
    return gtin14;
}
function readAttribute(key, text, def, context, errors) {
    if (def.tierMin !== null) {
        const known = TIERS.includes(def.tierMin);
        if (!known || planRank(context.tier) < planRank(def.tierMin)) {
            errors.push({
                kind: "attribute_above_tier",
                detail: `${key} needs the ${def.tierMin} plan or above. This account is on ${context.tier}.`,
            });
            return undefined;
        }
    }
    const { maxLength, pattern } = def.schema;
    if (typeof maxLength === "number" && text.length > maxLength) {
        errors.push({
            kind: "invalid_value",
            detail: `${key} is ${String(text.length)} characters. The limit is ${String(maxLength)}.`,
        });
        return undefined;
    }
    if (typeof pattern === "string" && !new RegExp(pattern, "u").test(text)) {
        errors.push({
            kind: "invalid_value",
            detail: `"${text}" is not a valid value for ${key}.`,
        });
        return undefined;
    }
    if (def.datatype === "measurement") {
        const measurement = readMeasurement(text);
        if (measurement === null) {
            errors.push({
                kind: "invalid_value",
                detail: `"${text}" is not a measurement for ${key}. Write a number and a unit, such as 500 g.`,
            });
            return undefined;
        }
        return measurement;
    }
    return def.langAware ? { en: text } : text;
}
/** Whether `validateRow` has a branch for `field`: a registry field, or `name.<tag>` / `description.<tag>`. */
export function isMappableImportField(field) {
    if (IMPORT_FIELD_REGISTRY.some((s) => s.field === field && s.group !== "language")) {
        return true;
    }
    const dot = field.indexOf(".");
    if (dot < 0)
        return false;
    return (IMPORT_FIELD_REGISTRY.some((s) => s.field === field.slice(0, dot) && s.group === "language") && LANG_TAG.test(field.slice(dot + 1)));
}
export function validateRow(raw, mapping, context) {
    const parsed = {};
    const errors = [];
    let sawGtin = false;
    for (const column of mapping.columns) {
        const field = column.field;
        if (field === null)
            continue;
        const cell = Object.hasOwn(raw, column.header) ? raw[column.header] : "";
        const text = cell.trim();
        if (field === "gtin14") {
            sawGtin = true;
            const gtin14 = readGtin(cell, context, errors);
            if (gtin14 !== undefined)
                parsed.gtin14 = gtin14;
            continue;
        }
        if (text === "")
            continue;
        if (field === "brandName" || field === "sku" || field === "gpcCategory") {
            parsed[field] = text;
        }
        else if (field === "netContent") {
            const measurement = readMeasurement(text);
            if (measurement === null) {
                errors.push({
                    kind: "invalid_value",
                    detail: `"${text}" is not a net content. Write a number and a unit, such as 500 g.`,
                });
            }
            else
                parsed.netContent = measurement;
        }
        else if (field.startsWith("name.") || field.startsWith("description.")) {
            const dot = field.indexOf(".");
            const group = field.slice(0, dot);
            const tag = field.slice(dot + 1);
            if (!LANG_TAG.test(tag)) {
                errors.push({
                    kind: "invalid_language",
                    detail: `"${tag}" is not a language tag. Use a code such as en, fr or pt-BR.`,
                });
            }
            else
                (parsed[group] ??= {})[tag] = text;
        }
        else if (field === "batch.lot") {
            const problem = checkAIValue("10", text);
            if (problem !== null) {
                errors.push({
                    kind: "invalid_lot",
                    detail: `Lot "${text}" cannot be a GS1 lot number: ${problem.message}`,
                });
            }
            else
                (parsed.batch ??= {}).lot = text;
        }
        else if (field === "batch.expiry" || field === "batch.mfgDate") {
            const read = readImportDate(text, context.dateOrder);
            if (!read.ok)
                errors.push({ kind: "unreadable_date", detail: read.detail });
            else
                (parsed.batch ??= {})[field === "batch.expiry" ? "expiry" : "mfgDate"] = read.iso;
        }
        else {
            const def = context.attributeDefs.get(field);
            if (def === undefined) {
                errors.push({
                    kind: "unknown_attribute",
                    detail: `"${field}" is not an attribute this platform holds.`,
                });
                continue;
            }
            const value = readAttribute(field, text, def, context, errors);
            if (value !== undefined)
                (parsed.attributes ??= {})[field] = value;
        }
    }
    if (!sawGtin) {
        errors.push({ kind: "invalid_value", detail: "No column is mapped to the GTIN." });
    }
    return { parsed, errors };
}
/** Every key `a` holds equals `b`'s, so a language the file does not carry is left alone. */
function sameValue(a, b) {
    if (a === b)
        return true;
    if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
        return false;
    }
    const left = a;
    const right = b;
    return Object.keys(left).every((key) => key === "value"
        ? Number(left[key]) === Number(right[key])
        : sameValue(left[key], right[key]));
}
/** Whether any mapped field of `parsed` differs from what the product holds. */
function differs(parsed, existing) {
    if (parsed.brandName !== undefined && parsed.brandName !== existing.brandName)
        return true;
    if (parsed.sku !== undefined && parsed.sku !== existing.sku)
        return true;
    if (parsed.gpcCategory !== undefined && parsed.gpcCategory !== existing.gpcCategory) {
        return true;
    }
    if (parsed.netContent !== undefined &&
        !sameValue(parsed.netContent, existing.netContent)) {
        return true;
    }
    for (const group of ["name", "description"]) {
        for (const [tag, text] of Object.entries(parsed[group] ?? {})) {
            if (existing[group][tag] !== text)
                return true;
        }
    }
    for (const [key, value] of Object.entries(parsed.attributes ?? {})) {
        if (!sameValue(value, existing.attributes[key]))
            return true;
    }
    const lot = parsed.batch?.lot;
    if (lot !== undefined) {
        const held = existing.batches.find((b) => b.lot === lot);
        if (held === undefined)
            return true;
        if (parsed.batch?.expiry !== undefined && parsed.batch.expiry !== held.expiry)
            return true;
        if (parsed.batch?.mfgDate !== undefined && parsed.batch.mfgDate !== held.mfgDate) {
            return true;
        }
    }
    return false;
}
/**
 * `create` when no product holds the GTIN, `update` when a mapped field differs, else `skip`.
 * A brand is required to create, and a row with none is `error` here because `validateRow`
 * cannot know whether the product exists.
 */
export function classifyRow(parsed, existing) {
    if (existing === undefined) {
        if (parsed.brandName === undefined) {
            return {
                action: "error",
                errors: [
                    {
                        kind: "invalid_value",
                        detail: "A brand is required to create a product, and this row has none.",
                    },
                ],
            };
        }
        return { action: "create", errors: [] };
    }
    return { action: differs(parsed, existing) ? "update" : "skip", errors: [] };
}
//# sourceMappingURL=import-validate.js.map